import dayjs from 'dayjs';
import {
	ExpertAcademicBoardResponse,
	ExpertEventResponse,
	ExpertEventResponseEventStatusEnum,
	ExpertEventTypeResponse,
	ExpertGroupMemberResponseTypeEnum,
	ExpertGroupWithMembersResponse,
	ExpertStudentResponse,
	StudentStatisticResponse
} from '@dopingtech/live_expert_bff_api_ts';
import { BoutiqueEventTagVariant } from './components/boutique-calendar/boutique-calendar.constants';
import { BoutiqueCalendarEvent, BoutiqueEventTag } from './components/boutique-calendar/boutique-calendar.types';
import { AcademicBoardExpert, ClassroomGroup, ExpertEventType, StudentPickerItem } from './boutique-classroom.types';
import { BoutiqueStudent } from './students/boutique-students.types';
import { UNASSIGNED_GROUP_LABEL } from './students/boutique-students.constants';
import {
	BOUTIQUE_GROUP_DEFAULT_GRADE_ID,
	EMPTY_VALUE_LABEL,
	EVENT_DATE_PARAM_FORMAT,
	EVENT_DAY_END_TIME,
	EVENT_PLATFORM_LABEL,
	EVENT_TIME_FORMAT,
	EVENT_PALETTE_SIZE,
	EVENT_PALETTE_TONE_PREFIX
} from './boutique-classroom.constants';

export function joinName(name?: string, surname?: string): string {
	return [name, surname].filter(Boolean).join(' ').trim();
}

function parseGradeId(value?: string): number | undefined {
	const parsed = Number(value);
	return Number.isFinite(parsed) && value !== undefined && value !== '' ? parsed : undefined;
}

export function mapExpertStudent(response: ExpertStudentResponse): BoutiqueStudent | null {
	if (response.studentId === undefined) return null;
	return {
		id: response.studentId,
		fullName: joinName(response.studentName, response.studentSurname),
		avatarUrl: response.avatarUrl,
		gradeId: parseGradeId(response.studentPreferredGradeId),
		gradeLabel: response.studentPreferredGradeName ?? EMPTY_VALUE_LABEL,
		groupId: response.studentGroupId ?? null,
		groupName: response.studentGroupName ?? null,
		schoolName: response.school?.schoolName ?? EMPTY_VALUE_LABEL,
		targetUniversity: response.target?.universityName ?? EMPTY_VALUE_LABEL,
		targetDepartment: response.target?.programName ?? EMPTY_VALUE_LABEL
	};
}

export function mapExpertGroup(response: ExpertGroupWithMembersResponse): ClassroomGroup | null {
	const id = response.group?.id;
	if (id === undefined) return null;
	const activeMembers = (response.members ?? []).filter(member => !member.deactivatedAt && member.memberId !== undefined && member.id !== undefined);
	const students = activeMembers.filter(member => member.type === ExpertGroupMemberResponseTypeEnum.Student);
	const experts = activeMembers.filter(member => member.type === ExpertGroupMemberResponseTypeEnum.Expert);
	return {
		id,
		name: response.group?.name ?? EMPTY_VALUE_LABEL,
		students: students.map(member => ({ id: member.memberId as number, fullName: joinName(member.name, member.surname), avatarUrl: member.avatarUrl })),
		experts: experts.map(member => ({
			id: member.memberId as number,
			groupMemberId: member.id as number,
			fullName: joinName(member.name, member.surname),
			avatarUrl: member.avatarUrl,
			branchId: member.branchId,
			branchName: member.branchName,
			liveServiceType: member.liveServiceType
		}))
	};
}

export function toStudentPickerItem(student: BoutiqueStudent, groups: ClassroomGroup[]): StudentPickerItem {
	return {
		id: student.id,
		fullName: student.fullName,
		groupId: student.groupId,
		groupName: groups.find(group => group.id === student.groupId)?.name ?? student.groupName ?? UNASSIGNED_GROUP_LABEL,
		schoolName: student.schoolName
	};
}

export function resolveGroupGradeId(students: BoutiqueStudent[]): number {
	return students.find(student => student.gradeId !== undefined)?.gradeId ?? BOUTIQUE_GROUP_DEFAULT_GRADE_ID;
}

export function mapExpertEvent(response: ExpertEventResponse): BoutiqueCalendarEvent | null {
	if (response.id === undefined || !response.eventStartDate || !response.eventEndDate) return null;
	const start = dayjs(response.eventStartDate);
	const end = dayjs(response.eventEndDate);
	if (!start.isValid() || !end.isValid()) return null;

	const status = response.eventStatus ?? ExpertEventResponseEventStatusEnum.Published;
	// Only the participation platform is shown as a tag; status and capacity tags were removed by design.
	const tagList: BoutiqueEventTag[] = response.eventPlatform ? [{ label: EVENT_PLATFORM_LABEL[response.eventPlatform] ?? response.eventPlatform, variant: BoutiqueEventTagVariant.BRANCH }] : [];

	return {
		id: response.id,
		dateKey: start.format(EVENT_DATE_PARAM_FORMAT),
		startTime: start.format(EVENT_TIME_FORMAT),
		endTime: end.isSame(start, 'day') ? end.format(EVENT_TIME_FORMAT) : EVENT_DAY_END_TIME,
		title: response.name ?? EMPTY_VALUE_LABEL,
		description: response.shortDescription ?? response.description,
		joinUrl: response.participationLink,
		platform: response.eventPlatform,
		eventTypeId: response.eventTypes?.[0]?.id,
		tagList,
		participantList: (response.participants ?? []).map((participant, index) => ({ id: index, name: joinName(participant.name, participant.surname) })),
		participantCount: response.registeredCount ?? response.participants?.length,
		groupNameList: response.groupNames ?? [],
		eventType: status
	};
}

export function mapStudentStatistic(response: StudentStatisticResponse): BoutiqueStudent | null {
	if (response.studentId === undefined) return null;
	return {
		id: response.studentId,
		fullName: joinName(response.studentName, response.studentSurname),
		avatarUrl: response.avatarUrl,
		gradeId: response.preferredGradeId,
		gradeLabel: response.preferredGradeName ?? EMPTY_VALUE_LABEL,
		groupId: response.studentGroupId ?? null,
		groupName: response.studentGroupName ?? null,
		schoolName: response.school?.schoolName ?? EMPTY_VALUE_LABEL,
		targetUniversity: response.target?.universityName ?? EMPTY_VALUE_LABEL,
		targetDepartment: response.target?.programName ?? EMPTY_VALUE_LABEL
	};
}

export function mapExpertEventType(response: ExpertEventTypeResponse): ExpertEventType | null {
	if (response.id === undefined) return null;
	return { id: response.id, code: response.code ?? '', name: response.name ?? EMPTY_VALUE_LABEL, isGroupEvent: Boolean(response.isGroupEvent) };
}

export function mapAcademicBoardExpert(response: ExpertAcademicBoardResponse): AcademicBoardExpert | null {
	if (response.expertId === undefined) return null;
	return {
		id: response.expertId,
		fullName: joinName(response.name, response.surname),
		avatarUrl: response.avatarUrl,
		branches: (response.branches ?? []).filter(branch => branch.id !== undefined).map(branch => ({ id: branch.id as number, name: branch.name ?? EMPTY_VALUE_LABEL }))
	};
}

export function toEventStartEnd(date: Date, startTime: Date, endTime: Date): { start: string; end: string } {
	const base = dayjs(date);
	const start = base.hour(startTime.getHours()).minute(startTime.getMinutes()).second(0).millisecond(0);
	const end = base.hour(endTime.getHours()).minute(endTime.getMinutes()).second(0).millisecond(0);
	return { start: start.toISOString(), end: end.toISOString() };
}

export function getEventPaletteTone(index: number): string {
	const safeIndex = Number.isFinite(index) && index >= 0 ? index % EVENT_PALETTE_SIZE : 0;
	return `${EVENT_PALETTE_TONE_PREFIX}-${safeIndex}`;
}
